import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatCDF } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Badge, Card, StatusBadge } from "../../components/ui-kit";
import {
  CarProfile,
  CreditCard,
  FileText,
  ShieldWarning,
  UsersThree,
  Wallet,
} from "@phosphor-icons/react";

function DataBlock({ title, count, children }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 p-4">
        <div className="font-heading text-lg font-black">{title}</div>
        <Badge tone="primary">{count}</Badge>
      </div>
      <div className="max-h-[480px] space-y-2 overflow-y-auto p-3">{children}</div>
    </Card>
  );
}

function Empty() {
  return <div className="p-4 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">Aucune donnee</div>;
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/admin/overview")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const users = useMemo(() => (data?.users || []).filter((u) =>
    !q || [u.name, u.email, u.phone, u.role].some((v) => String(v || "").toLowerCase().includes(q))
  ), [data, q]);
  const drivers = useMemo(() => (data?.drivers || []).filter((d) =>
    !q || [d.user?.name, d.user?.email, d.user?.phone, d.vehicle?.plate, d.vehicle?.model].some((v) => String(v || "").toLowerCase().includes(q))
  ), [data, q]);

  const stats = data?.stats || {};
  const quick = [
    { label: "Utilisateurs", value: stats.total_users || 0, icon: UsersThree, to: "/admin/users" },
    { label: "Chauffeurs", value: stats.total_drivers || 0, icon: CarProfile, to: "/admin/drivers" },
    { label: "En attente", value: stats.pending_drivers || 0, icon: FileText, to: "/admin/documents" },
    { label: "Courses", value: stats.total_rides || 0, icon: Wallet, to: "/admin/rides" },
    { label: "Actives", value: stats.active_rides || 0, icon: ShieldWarning, to: "/admin/rides" },
    { label: "Revenus", value: formatCDF(stats.total_revenue || 0), icon: CreditCard, to: "/admin/payments" },
  ];

  return (
    <AdminShell title="Gestion">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Gestion totale</h1>
          <p className="mt-1 text-sm text-muted-foreground">Vue admin de toutes les donnees principales.</p>
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher nom, email, telephone, plaque..."
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm outline-none focus:border-primary sm:max-w-sm"
        />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {quick.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.label} to={item.to}>
              <Card className="h-full p-4 hover:bg-white/10">
                <div className="flex items-center justify-between">
                  <Icon size={22} weight="duotone" className="text-primary" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">ouvrir</span>
                </div>
                <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">{item.label}</div>
                <div className="truncate font-heading text-2xl font-black">{item.value}</div>
              </Card>
            </Link>
          );
        })}
      </div>

      {loading ? (
        <Card className="mt-6 p-8 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">Chargement...</Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <DataBlock title="Utilisateurs" count={users.length}>
            {users.length === 0 && <Empty />}
            {users.map((u) => (
              <div key={u.id} className="rounded-2xl bg-white/5 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-bold">{u.name}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">{u.email}</div>
                    <div className="font-mono text-xs text-muted-foreground">{u.phone}</div>
                  </div>
                  <Badge tone={u.role === "admin" ? "success" : "default"}>{u.role}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  <Badge tone={u.email_verified ? "success" : "warning"}>email {u.email_verified ? "ok" : "non"}</Badge>
                  <Badge tone={u.phone_verified ? "success" : "warning"}>tel {u.phone_verified ? "ok" : "non"}</Badge>
                </div>
              </div>
            ))}
          </DataBlock>

          <DataBlock title="Chauffeurs" count={drivers.length}>
            {drivers.length === 0 && <Empty />}
            {drivers.map((d) => (
              <div key={d.user.id} className="rounded-2xl bg-white/5 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-bold">{d.user.name}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">{d.user.email}</div>
                    <div className="font-mono text-xs text-muted-foreground">{d.user.phone}</div>
                  </div>
                  <StatusBadge status={d.profile?.approval_status || "PENDING_VALIDATION"} />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <div>Plaque: <span className="font-mono text-foreground">{d.vehicle?.plate || "-"}</span></div>
                  <div>Modele: <span className="font-mono text-foreground">{d.vehicle?.model || "-"}</span></div>
                  <div>Docs: <span className="font-mono text-foreground">{d.documents?.length || 0}</span></div>
                  <div>Status: <span className="font-mono text-foreground">{d.profile?.status || "offline"}</span></div>
                </div>
              </div>
            ))}
          </DataBlock>

          <DataBlock title="Courses recentes" count={data?.rides?.length || 0}>
            {(data?.rides || []).length === 0 && <Empty />}
            {(data?.rides || []).map((r) => (
              <div key={r.id} className="rounded-2xl bg-white/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-xs font-bold">#{r.id.slice(0, 8).toUpperCase()}</div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-1 text-xs">{r.customer_name || "-"} -> {r.driver_name || "sans chauffeur"}</div>
                <div className="mt-1 font-mono text-xs text-primary">{formatCDF(r.price)} · {r.payment_method} · {r.payment_status}</div>
              </div>
            ))}
          </DataBlock>

          <DataBlock title="Paiements" count={data?.payments?.length || 0}>
            {(data?.payments || []).length === 0 && <Empty />}
            {(data?.payments || []).map((p) => (
              <div key={p.id} className="rounded-2xl bg-white/5 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-xs font-bold">{formatCDF(p.amount)}</div>
                  <Badge tone={p.status === "COMPLETED" ? "success" : "warning"}>{p.status || p.type}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{p.method || p.provider || p.description || "transaction"}</div>
              </div>
            ))}
          </DataBlock>
        </div>
      )}
    </AdminShell>
  );
}
